import { OrderPlanningRowAction, ReasonMap } from "./OrderPlanningRowAction";
import { Button, DataSource, TableRow } from "@mcleod/components";
import { PlanningTableRowActionProps } from "@mcleod/general/src/planning/PlanningTableRowAction";
import { ActionPermission, Api, ModelRow, StringUtil } from "@mcleod/core";
import { RowDispatchControlFields } from "@mcleod/general/src/models/autogen/AutogenModelDispatchControl";
import { CommonDialogs } from "@mcleod/common";
import { OrderMultiSelectErrorModal } from "@mcleod/general/src/OrderMultiSelectErrorModal";

export class RowActionHold extends OrderPlanningRowAction {
    override getButton(): Button {
        return this.toolPanel.buttonHold;
    }

    override getButtonId(): string {
        return "buttonHold";
    }

    override getFieldsUsedInThisFile(): string[] {
        return ["hold", "hold_reason", "credit_override_status", "manifest_id", "order_id"];
    }

    override getMaxSelections(): keyof RowDispatchControlFields {
        return "max_a_hold_selections";
    }

    override getMultiSelectPermission(): ActionPermission {
        return ActionPermission.ACTION_MULTISELECT_HOLD;
    }

    override actionValidateSelection(selectedRows: ModelRow[], reasonMap: ReasonMap): void {
        const isOnHold = selectedRows[0]?.getBoolean("hold", false);
        const matchedStatuses = selectedRows.every(row => isOnHold == row.getBoolean("hold", false));
        if (!matchedStatuses) {
            this.addReason(reasonMap, "mixed_hold_status", "All selected rows must have the same hold status.");
            return;
        }
        this.getButton().displayLabel = isOnHold ? "Remove Hold" : "Apply Hold";

        selectedRows.forEach(row => this.rowValidator(reasonMap, row));
    }

    private rowValidator (reasonMap: ReasonMap, modelRow: ModelRow) {
        const arrayField = "order_id";

        if (!this.hasUpdateOrderPermissions(modelRow, {xfer2BillDateField: "xferred_2_bill_date"})) {
            this.addReason(reasonMap, "order_update_perms", "You do not have permission to update orders [{0}].", modelRow, arrayField);
        }

        // Note: Transferred to settlements will always be false for available orders
        if (!this.hasUpdateMovementPermissions(modelRow, {xfer2SettleDateField: "xferred_2_settle_date", billDateField: "bill_date"})) {
            this.addReason(reasonMap, "move_update_perms", "You do not have permission to update movements for orders [{0}].", modelRow, arrayField);
        }

        if (modelRow.getBoolean("hold", false) && modelRow.get("credit_override_status") === "P") {
            this.addReason(reasonMap, "credit_override", "Hold cannot be removed from orders [{0}] while credit override is pending.", modelRow, arrayField);
        }

        if (!modelRow.isNull("manifest_id")) {
            this.addReason(reasonMap, "manifested", "Manifests [{0}] cannot be placed on hold.", modelRow, "manifest_id");
        }
    }

    override async execute(props: PlanningTableRowActionProps) {
        const selectedRows = props.selectedRows;
        const isOnHold = selectedRows[0]?.data.getBoolean("hold", false);
        if (isOnHold) {
            await this.updateSelectedOrderHoldStatus(selectedRows, false, "");
        } else {
            CommonDialogs.showInputDialog(
                `Enter the reason for placing order${selectedRows.length > 1 ? "s" : ""} on hold`,
                "On Hold Reason",
                {
                    required: true,
                    field: "hold_reason",
                    dataSource: new DataSource({ id: "ordersDataSource", url: "lme/dispatch/orders" }),
                }, {
                    okCaption: selectedRows.length > 1 ? "Apply Holds" : "Apply Hold",
                    panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" },
                    okProps: { backgroundColor: "primary", color: "primary.reverse", width: 110 },
                }
            ).then(async (reason) => {
                if (reason != null)
                    await this.updateSelectedOrderHoldStatus(selectedRows, true, reason)
            });
        }
    }

    private updateSelectedOrderHoldStatus(selectedRows: TableRow[], onHold: boolean, holdReason: string, showResults: boolean = true): Promise<void> {
        const inProgressSnackbar = this.showInProgressSnackbar(`Updating hold status for ${selectedRows.length} order(s)...`);

        return Api.post("lme/dispatch/order-holds/batch", {
            "keys": selectedRows.map(row => row.data.getString("order_id", null)).filter(id => !!id).map(id => ({ order_id: id })),
            "data": {
                "hold": onHold,
                "hold_reason": holdReason
            }
        }).then((result) => {
            if (!showResults) return;

            const updated = result.data.filter(result => StringUtil.isEmptyString(result.message));

            const failedResults = result.data.filter(result => !StringUtil.isEmptyString(result.message) && result.retry);
            const failedRows = failedResults.map(result => {
                const row = selectedRows.find(row => row.data.getString("order_id") === result.keys.order_id)
                row.data.set("error", result.message);
                return row;
            });

            const erroredResults = result.data.filter(result => !StringUtil.isEmptyString(result.message) && !result.retry);
            const erroredRows = erroredResults.map(result => {
                const row = selectedRows.find(row => row.data.getString("order_id") === result.keys.order_id)
                const modelRow = row.data.createBasicCopy();
                modelRow.set("error", result.message);
                return modelRow;
            });

            const retryAction = (selectedErrorRows: TableRow[]) => this.updateSelectedOrderHoldStatus(selectedErrorRows, onHold, holdReason, showResults);
            OrderMultiSelectErrorModal.showModal(failedRows, erroredRows, updated.length, "On Hold Status", "Retry", retryAction, undefined, true);
        }).finally(() => {
            if (selectedRows.length > 1) {
                selectedRows[0].table.selectedRows = [];
            }
            inProgressSnackbar.dismiss()
        });
    }
}
